import clsx from 'clsx';
import { CalendarClock, Clock, Flame, Users } from 'lucide-react';
import type { Reservation, RestaurantTable, TableStatus } from '@sebastian/shared';
import { formatClock, formatWaiting } from '../lib/format';
import { useCurrency } from '../lib/currency';

const STATUS_STYLE: Record<TableStatus, { ring: string; dot: string; label: string; text: string }> = {
  AVAILABLE: {
    ring: 'border-line hover:border-available/60',
    dot: 'bg-available',
    label: 'Available',
    text: 'text-available',
  },
  OCCUPIED: {
    ring: 'border-occupied/50 hover:border-occupied bg-occupied/[0.06]',
    dot: 'bg-occupied',
    label: 'Occupied',
    text: 'text-occupied',
  },
  WAITING_PAYMENT: {
    ring: 'border-payment/60 hover:border-payment bg-payment/[0.08]',
    dot: 'bg-payment',
    label: 'Waiting for Payment',
    text: 'text-payment',
  },
  RESERVED: {
    ring: 'border-reserved/50 hover:border-reserved bg-reserved/[0.06]',
    dot: 'bg-reserved',
    label: 'Reserved',
    text: 'text-reserved',
  },
};

export function TableCard({
  table,
  now,
  booking,
  onSelect,
}: {
  table: RestaurantTable;
  now: number;
  /** The next booking held on this table in the coming hours, if any. */
  booking?: Reservation | null;
  onSelect: (table: RestaurantTable) => void;
}) {
  const style = STATUS_STYLE[table.status];
  const { money } = useCurrency();
  const order = table.activeOrder;

  return (
    <button
      type="button"
      onClick={() => onSelect(table)}
      className={clsx(
        'group relative flex min-h-[10.5rem] flex-col rounded-card border bg-surface p-4 text-left',
        'transition-all duration-150 hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0',
        style.ring,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-display text-3xl leading-none text-ink">
            {String(table.number).padStart(2, '0')}
          </div>
          <div className="mt-1 text-xs text-ink-faint">{table.section ?? table.name}</div>
        </div>
        <span className={clsx('mt-1 h-2.5 w-2.5 shrink-0 rounded-full', style.dot)} />
      </div>

      <div className={clsx('mt-3 text-[11px] font-semibold uppercase tracking-wider', style.text)}>
        {style.label}
      </div>

      {booking ? (
        <div
          className="mt-2 inline-flex max-w-full items-center gap-1 self-start truncate rounded-full border border-reserved/50 bg-reserved/10 px-2 py-0.5 text-[11px] font-medium text-reserved"
          title={booking.notes ?? undefined}
        >
          <CalendarClock size={11} className="shrink-0" />
          <span className="truncate">
            Booked {formatClock(booking.reservedAt)} · {booking.guestName} · {booking.partySize}
          </span>
        </div>
      ) : null}

      {order ? (
        <div className="mt-auto space-y-1.5 pt-3">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-soft">
            <span className="inline-flex items-center gap-1">
              <Users size={13} /> {order.guestCount}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock size={13} /> {formatClock(order.openedAt)}
            </span>
            <span className="tnum ml-auto text-ink-faint">{formatWaiting(order.openedAt, now)}</span>
          </div>
          <div className="flex flex-wrap items-end justify-between gap-x-2 gap-y-1">
            <span className="tnum whitespace-nowrap font-display text-xl text-accent-strong">
              {money(order.grandTotalCents)}
            </span>
            {order.unsentItemCount > 0 ? (
              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-caution/50 bg-caution/12 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-caution">
                <Flame size={11} /> {order.unsentItemCount} unsent
              </span>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="mt-auto pt-3">
          <div className="text-xs text-ink-faint">Seats {table.capacity}</div>
          <div className="mt-1 text-sm font-medium text-ink-soft opacity-0 transition-opacity group-hover:opacity-100">
            Tap to open →
          </div>
        </div>
      )}
    </button>
  );
}
